/**
 * Search drawer.
 *
 * Opens from the header's magnifier and filters the pieces rendered into it: the field
 * matches the name and the colourway, the three selects narrow by category, size and
 * order, and the checkbox drops anything sold out. Everything is done against the
 * markup already on the page, so there is no wait between keystrokes.
 *
 * The category and size lists are built from the pieces themselves, so a filter is
 * never offered that would return nothing.
 */
(function () {
  const drawer = document.querySelector('[data-search-drawer]');
  if (!drawer) return;

  const panel = drawer.querySelector('.as-drawer__panel');
  const input = drawer.querySelector('[data-search-input]');
  const typeSel = drawer.querySelector('[data-search-type]');
  const sizeSel = drawer.querySelector('[data-search-size]');
  const sortSel = drawer.querySelector('[data-search-sort]');
  const availBox = drawer.querySelector('[data-search-available]');
  const countEl = drawer.querySelector('[data-search-count]');
  const clearBtn = drawer.querySelector('[data-search-clear]');
  const grid = drawer.querySelector('[data-search-grid]');
  const empty = drawer.querySelector('[data-search-empty]');
  const items = grid ? [...grid.querySelectorAll('[data-search-item]')] : [];

  // Remember the order the merchant put them in, so "Our edit" can be restored.
  items.forEach((item, i) => {
    item.dataset.order = String(i);
  });

  // The rested listing, to come back to when the field is cleared.
  const restingHTML = grid ? grid.innerHTML : '';
  let live = items;
  let searchToken = 0;

  const title = (s) => s.replace(/\b[a-z]/g, (c) => c.toUpperCase());

  function fillFilters() {
    if (!items.length) return;
    const types = new Set();
    const sizes = new Set();
    items.forEach((item) => {
      if (item.dataset.type) types.add(item.dataset.type);
      (item.dataset.sizes || '').split('|').filter(Boolean).forEach((s) => sizes.add(s));
    });
    [...types].sort().forEach((t) => {
      const o = document.createElement('option');
      o.value = t;
      o.textContent = title(t);
      typeSel.appendChild(o);
    });
    // Letter sizes in wearing order, then numeric waists, then "one size" last.
    const order = ['xxs', 'xs', 's', 'm', 'l', 'xl', 'xxl', '2xl', '3xl'];
    const rank = (v) => {
      if (v === 'one size') return 3000;
      const i = order.indexOf(v);
      if (i !== -1) return i;
      const n = parseFloat(v);
      return isNaN(n) ? 2000 : 1000 + n;
    };
    [...sizes]
      .sort((a, b) => rank(a) - rank(b) || a.localeCompare(b))
      .forEach((s) => {
        const o = document.createElement('option');
        o.value = s;
        o.textContent = s === 'one size' ? 'One size' : s.toUpperCase();
        sizeSel.appendChild(o);
      });
  }

  function apply() {
    if (!live.length) {
      countEl.textContent = '0 pieces';
      if (empty) empty.hidden = false;
      syncClear();
      return;
    }
    const q = (input.value || '').trim().toLowerCase();
    const type = typeSel.value;
    const size = sizeSel.value;
    const availableOnly = availBox.checked;
    let shown = 0;

    live.forEach((item) => {
      // The catalogue search has already matched the words; the field only narrows
      // the rested listing.
      const matchesText = searching || !q || item.dataset.title.includes(q) || item.dataset.colour.includes(q);
      const matchesType = !type || item.dataset.type === type;
      const matchesSize = !size || (item.dataset.sizes || '').split('|').includes(size);
      const matchesStock = !availableOnly || item.dataset.available === 'true';
      const show = matchesText && matchesType && matchesSize && matchesStock;
      item.hidden = !show;
      if (show) shown += 1;
    });

    const sort = sortSel.value;
    const sorted = [...live].sort((a, b) => {
      if (sort === 'asc') return Number(a.dataset.price) - Number(b.dataset.price);
      if (sort === 'desc') return Number(b.dataset.price) - Number(a.dataset.price);
      return Number(a.dataset.order) - Number(b.dataset.order);
    });
    sorted.forEach((item) => grid.appendChild(item));

    countEl.textContent = `${shown} ${shown === 1 ? 'piece' : 'pieces'}`;
    if (empty) empty.hidden = shown !== 0;
    syncClear();
  }

  // Clear filters only means something once something is set.
  function syncClear() {
    if (!clearBtn) return;
    const dirty = (input.value || '').trim() !== '' || typeSel.value !== '' || sizeSel.value !== ''
      || sortSel.value !== 'edit' || !availBox.checked;
    clearBtn.hidden = !dirty;
  }

  let searching = false;

  // The field searches the whole catalogue, not just the collection rendered at rest.
  async function runSearch() {
    const q = (input.value || '').trim();
    const token = ++searchToken;
    if (q.length < 2) {
      searching = false;
      if (grid.innerHTML !== restingHTML) {
        grid.innerHTML = restingHTML;
        live = [...grid.querySelectorAll('[data-search-item]')];
        live.forEach((item, i) => { item.dataset.order = String(i); });
      } else {
        live = [...grid.querySelectorAll('[data-search-item]')];
      }
      apply();
      return;
    }
    searching = true;
    try {
      const url = `${(window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/'}search?q=${encodeURIComponent(q)}&type=product&options[unavailable_products]=show&section_id=atelier-search-results`;
      const res = await fetch(url);
      if (!res.ok) throw new Error(`search ${res.status}`);
      const markup = await res.text();
      if (token !== searchToken) return;
      const next = new DOMParser().parseFromString(markup, 'text/html').querySelector('[data-search-results]');
      grid.innerHTML = next ? next.innerHTML : '';
      live = [...grid.querySelectorAll('[data-search-item]')];
      live.forEach((item, i) => { item.dataset.order = String(i); });
      apply();
    } catch (error) {
      if (token !== searchToken) return;
      console.log(`search: ${error.message}`);
    }
  }

  let typingTimer;

  function open() {
    drawer.hidden = false;
    void drawer.offsetWidth;
    requestAnimationFrame(() => drawer.classList.add('as-drawer--open'));
    document.body.classList.add('overflow-hidden');
    document.addEventListener('keydown', onKeydown);
    setTimeout(() => input && input.focus(), 380);
  }

  function close() {
    drawer.classList.remove('as-drawer--open');
    document.body.classList.remove('overflow-hidden');
    document.removeEventListener('keydown', onKeydown);
    // Kept in the DOM until the slide finishes, so it does not vanish mid-transition.
    setTimeout(() => {
      if (!drawer.classList.contains('as-drawer--open')) drawer.hidden = true;
    }, 400);
  }

  function onKeydown(event) {
    if (event.key === 'Escape') close();
  }

  // The header's own search is a <details> modal; on this page the magnifier opens the
  // drawer instead, so its toggle is intercepted rather than removed.
  document.addEventListener(
    'click',
    (event) => {
      const summary = event.target.closest('.header__icon--search, [data-search-open]');
      if (summary && !event.target.closest('[data-search-drawer]')) {
        event.preventDefault();
        event.stopPropagation();
        const details = summary.closest('details');
        if (details) details.open = false;
        open();
        return;
      }
      if (event.target.closest('[data-search-close]')) {
        event.preventDefault();
        close();
      }
    },
    true
  );

  if (input) {
    input.addEventListener('input', () => {
      clearTimeout(typingTimer);
      typingTimer = setTimeout(runSearch, 220);
      syncClear();
    });
  }
  [typeSel, sizeSel, sortSel, availBox].forEach((el) => el && el.addEventListener('change', apply));
  if (clearBtn) {
    clearBtn.addEventListener('click', () => {
      input.value = '';
      typeSel.value = '';
      sizeSel.value = '';
      sortSel.value = 'edit';
      availBox.checked = true;
      runSearch();
    });
  }

  fillFilters();
  apply();
  syncClear();
})();
